import { useState } from "react";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { getFirebaseFunctions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";

export default function SettingsPage() {
  const [backupLoading, setBackupLoading] = useState(false);
  const [cleanupLoading, setCleanupLoading] = useState(false);
  const [backupName, setBackupName] = useState("");
  const [retentionDays, setRetentionDays] = useState("90");
  const [dryRun, setDryRun] = useState(true);

  const handleBackup = async () => {
    try {
      setBackupLoading(true);
      const functions = getFirebaseFunctions();
      if (!functions) {
        toast.error("Firebase not initialized");
        setBackupLoading(false);
        return;
      }

      const adminOperations = httpsCallable(functions, "adminOperations");
      const result = await adminOperations({
        action: "backupDatabase",
        data: {
          backupName: backupName || undefined,
        },
      });
      toast.success((result.data as Record<string, unknown>).message as string);
      setBackupName("");
    } catch (error) {
      toast.error("Failed to initiate backup");
      console.error(error);
    } finally {
      setBackupLoading(false);
    }
  };

  const handleCleanup = async () => {
    try {
      setCleanupLoading(true);
      const functions = getFirebaseFunctions();
      if (!functions) {
        toast.error("Firebase not initialized");
        setCleanupLoading(false);
        return;
      }

      const adminOperations = httpsCallable(functions, "adminOperations");
      const result = await adminOperations({
        action: "cleanupDeletedData",
        data: {
          olderThanDays: parseInt(retentionDays),
        },
      });
      toast.success((result.data as Record<string, unknown>).message as string);
    } catch (error) {
      toast.error("Failed to cleanup deleted data");
      console.error(error);
    } finally {
      setCleanupLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Database Backup */}
      <Card>
        <CardHeader>
          <CardTitle>Database Backup</CardTitle>
          <CardDescription>
            Create backups of your database for disaster recovery
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert>
            <CheckCircle2 className="h-4 w-4" />
            <AlertDescription>
              Backups are stored in Cloud Storage. You can restore from backups
              in case of data loss.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="backup-name">Backup Name (Optional)</Label>
            <Input
              id="backup-name"
              placeholder="e.g., backup-2024-11-15"
              value={backupName}
              onChange={(e) => setBackupName(e.target.value)}
            />
          </div>

          <Button
            onClick={handleBackup}
            disabled={backupLoading}
            className="w-full"
          >
            {backupLoading ? "Creating Backup..." : "Create Backup Now"}
          </Button>
        </CardContent>
      </Card>

      {/* Data Cleanup */}
      <Card>
        <CardHeader>
          <CardTitle>Cleanup Deleted Data</CardTitle>
          <CardDescription>
            Remove soft-deleted data older than retention period (GDPR)
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              This action removes data that was previously deleted. Data older
              than the retention period will be permanently removed.
            </AlertDescription>
          </Alert>

          <div className="space-y-2">
            <Label htmlFor="retention-days">Retention Days</Label>
            <Input
              id="retention-days"
              type="number"
              min="7"
              max="365"
              value={retentionDays}
              onChange={(e) => setRetentionDays(e.target.value)}
            />
            <p className="text-sm text-muted-foreground">
              Data deleted more than {retentionDays} days ago will be
              permanently removed
            </p>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="dry-run"
              checked={dryRun}
              onChange={(e) => setDryRun(e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="dry-run" className="font-normal">
              Dry run (don't actually delete, just show what would be deleted)
            </Label>
          </div>

          <Button
            onClick={handleCleanup}
            disabled={cleanupLoading}
            variant={dryRun ? "outline" : "destructive"}
            className="w-full"
          >
            {cleanupLoading
              ? "Processing..."
              : `${dryRun ? "Preview" : "Execute"} Cleanup`}
          </Button>
        </CardContent>
      </Card>

      {/* System Info */}
      <Card>
        <CardHeader>
          <CardTitle>System Information</CardTitle>
          <CardDescription>
            Admin dashboard and Cloud Functions information
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Admin Functions
              </p>
              <p className="text-lg font-bold">12 Functions</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                API Version
              </p>
              <p className="text-lg font-bold">Firebase v2</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Database
              </p>
              <p className="text-lg font-bold">Firestore</p>
            </div>
            <div>
              <p className="text-sm font-medium text-muted-foreground">
                Storage
              </p>
              <p className="text-lg font-bold">Cloud Storage</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
